
import { useIdentity } from "@/context/identity-context";
import { useLocalization } from "@/context/localization-context";
import { usePermissions } from "@/hooks/use-permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function UserRoleInfo() {
  const { identity, roles, rolesLoading } = useIdentity();
  const { t } = useLocalization();
  const {
    permissions,
    userRole,
    isAdmin,
    isDeveloper,
    isBranchOwner,
    isOperator
  } = usePermissions();
  const yesNo = (value: boolean | undefined) => (value ? t("common.yes") : t("common.no"));

  if (!identity) {
    return <div>{t("userInfo.signInRequired")}</div>;
  }

  if (rolesLoading) {
    return <div>{t("userInfo.loadingRoles")}</div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("userInfo.user.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p><strong>{t("userInfo.user.email")}:</strong> {identity.email}</p>
          <p><strong>{t("userInfo.user.userId")}:</strong> {identity.payload?.user_id}</p>
          <p><strong>{t("userInfo.user.roleId")}:</strong> {identity.payload?.role_id}</p>
          <p><strong>{t("userInfo.user.branchId")}:</strong> {identity.payload?.branch_id}</p>
        </CardContent>
      </Card>

      {userRole && (
        <Card>
          <CardHeader>
            <CardTitle>{t("userInfo.role.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p><strong>{t("userInfo.role.role")}:</strong> {userRole.description}</p>
            <p><strong>{t("userInfo.role.enabled")}:</strong> {yesNo(userRole.enabled)}</p>
            <div>
              <strong>{t("userInfo.role.permissions")}:</strong>
              <ul className="list-disc list-inside ml-4 mt-1">
                {permissions.map((permission) => (
                  <li key={permission}>{permission}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("userInfo.classification.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p><strong>{t("userInfo.classification.admin")}:</strong> {yesNo(isAdmin)}</p>
          <p><strong>{t("userInfo.classification.developer")}:</strong> {yesNo(isDeveloper)}</p>
          <p><strong>{t("userInfo.classification.branchOwner")}:</strong> {yesNo(isBranchOwner)}</p>
          <p><strong>{t("userInfo.classification.operator")}:</strong> {yesNo(isOperator)}</p>
        </CardContent>
      </Card>

      {roles && (
        <Card>
          <CardHeader>
            <CardTitle>{t("userInfo.roles.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {roles.map((role) => (
                <div key={role._id} className="border p-2 rounded">
                  <p><strong>{role.description}</strong> ({role._id})</p>
                  <p className="text-sm text-muted-foreground">
                    {t("userInfo.roles.permissions", { permissions: role.permissions.join(", ") })}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
